import {
  Flex,
  HStack,
  useDisclosure,
  VStack,
  useColorModeValue,
  CloseButton,
} from '@chakra-ui/react';
import React from 'react';

import { UploadReceivablesFileForm } from '../UploadReceivablesFileForm';
import {
  ReceivableUpload,
  LoanProductCreditFacilityReceivableUploadMap,
  UploadState,
  UploadFileModal,
  UploadFileErrorAlert,
  initialUploadState,
  ActionsDrawerState,
  ActionsDrawerShell,
  ActionsDrawerHeader,
  ActionsDrawerFooter,
} from './shared';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';

export const UploadRepurchaseFileInput = ({
  receivableUpload,
  validateFileStructure,
  setReceivableUploadsMap,
  kickingOffJob,
}: {
  receivableUpload: ReceivableUpload;
  validateFileStructure: (
    file: File,
    receivableUpload: ReceivableUpload,
  ) => Promise<void>;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  kickingOffJob: boolean;
}) => {
  const uploadFileModal = useDisclosure();

  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');

  return (
    <>
      <VStack
        as="span"
        alignItems="flex-start"
        spacing={1}
        borderRadius="8px"
        padding="16px"
        border={`1px solid ${borderColor}`}
        bg={backgroundColor}
        width="100%"
        mb={receivableUpload.fileError ? '0px' : '16px'}
      >
        <HStack width="100%" justifyContent="space-between">
          <Typography TextProps={{ fontWeight: 500 }}>
            {receivableUpload.loanProductName}
          </Typography>
          <UploadState
            receivableUpload={receivableUpload}
            openUploadModal={() => uploadFileModal.onOpen()}
            kickingOffJob={kickingOffJob}
          />
          <UploadFileModal
            isOpen={uploadFileModal.isOpen}
            onClose={uploadFileModal.onClose}
          >
            <UploadReceivablesFileForm
              onClose={uploadFileModal.onClose}
              handleFile={(file) => {
                validateFileStructure(file, receivableUpload);
              }}
            />
          </UploadFileModal>
        </HStack>
        <Flex
          mt="10px"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Typography variant="base-muted" TextProps={{ fontSize: '14px' }}>
            Balance Sheet
          </Typography>
          {receivableUpload.file && (
            <Flex alignItems="center">
              <Typography
                variant="base-muted"
                TextProps={{ fontSize: '14px', mr: '8px' }}
              >
                {receivableUpload.file?.name}
              </Typography>
              <CloseButton
                onClick={() => {
                  setReceivableUploadsMap((receivableUploadsMap) => {
                    return {
                      ...receivableUploadsMap,
                      [receivableUpload.key]: {
                        ...receivableUploadsMap[receivableUpload.key],
                        ...initialUploadState,
                      },
                    };
                  });
                }}
                colorScheme="gray"
                isDisabled={kickingOffJob}
              />
            </Flex>
          )}
        </Flex>
      </VStack>
      {receivableUpload.fileError && <UploadFileErrorAlert />}
    </>
  );
};

export const RepurchaseDrawer = ({
  onClose,
  receivableUploadsMap,
  setReceivableUploadsMap,
  validateFileStructure,
  kickingOffJob,
  initiateReassignmentConfirmationJob,
}: {
  onClose: () => void;
  receivableUploadsMap: LoanProductCreditFacilityReceivableUploadMap;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  validateFileStructure: (
    file: File,
    receivableUpload: ReceivableUpload,
  ) => Promise<void>;
  kickingOffJob: boolean;
  initiateReassignmentConfirmationJob: (
    jobType: ActionsDrawerState,
  ) => Promise<void>;
}) => {
  const submitDisabled = Object.values(receivableUploadsMap).some(
    (upload) => !upload.isValid || !upload.file,
  );

  return (
    <ActionsDrawerShell onClose={onClose}>
      <ActionsDrawerHeader onClose={onClose} actionsDrawerState="repurchase" />
      <Flex p="40px" direction="column" justifyContent="space-between" h="100%">
        <Flex direction="column">
          <Typography variant="base-muted">
            Upload a header-less CSV file with a single column containing the
            Receivable IDs you would like to repurchase and move to the balance
            sheet.
          </Typography>
          <Flex mt="40px" direction="column" gap="24px">
            <Typography TextProps={{ fontSize: '16px', fontWeight: 600 }}>
              Upload Files
            </Typography>
            <VStack spacing="8px" alignItems="flex-start" width="100%">
              {Object.values(receivableUploadsMap).map((receivableUpload) => {
                return (
                  <UploadRepurchaseFileInput
                    key={receivableUpload.key}
                    validateFileStructure={validateFileStructure}
                    receivableUpload={receivableUpload}
                    setReceivableUploadsMap={setReceivableUploadsMap}
                    kickingOffJob={kickingOffJob}
                  />
                );
              })}
            </VStack>
          </Flex>
        </Flex>
        <ActionsDrawerFooter
          onSubmit={async () => {
            await initiateReassignmentConfirmationJob('repurchase');
            onClose();
          }}
          kickingOffJob={kickingOffJob}
          submitDisabled={submitDisabled}
        />
      </Flex>
    </ActionsDrawerShell>
  );
};
